// Session 0.5 study-deck content.
// Sources: Plan/transformer-circuits-52week-plan.md, phase 0, plus the four Distill close-reads.

export type Slide =
  | { kind: "cover"; chapter: string; title: string; subtitle: string; meta: string[] }
  | { kind: "statement"; chapter: string; title: string; lead: string; body?: string[]; aside?: string }
  | { kind: "bullets"; chapter: string; title: string; lead?: string; items: { term: string; text: string }[] }
  | { kind: "cards"; chapter: string; title: string; lead?: string; cards: { label: string; term: string; text: string }[] }
  | { kind: "numbered"; chapter: string; title: string; lead?: string; items: { term: string; text: string }[] }
  | { kind: "table"; chapter: string; title: string; lead?: string; head: string[]; rows: string[][] }
  | { kind: "links"; chapter: string; title: string; lead?: string; groups: { label: string; note: string; items: { title: string; meta: string; href?: string; text: string }[] }[] }
  | { kind: "split"; chapter: string; title: string; left: { label: string; text: string[] }; right: { label: string; text: string[] } }
  | { kind: "quote"; chapter: string; title: string; quote: string; attribution: string; body?: string[] }
  | { kind: "code"; chapter: string; title: string; lead?: string; code: string; caption?: string; points?: { term: string; text: string }[] }
  | { kind: "figure"; chapter: string; title: string; lead?: string; figure: "residual-stream" | "path-expansion"; points?: { term: string; text: string }[] }
  | { kind: "closing"; chapter: string; title: string; lead: string; items: { term: string; text: string }[]; footer: string };

export type DeckSlide = Slide & { note?: string };

/** Per-deck metadata; each deck exports one of these next to its slides. */
export type DeckMeta = {
  session: string;
  title: string;
  phase: string;
  window: string;
  hours: string;
  source: string;
  /** Where the deck's back link goes and what it says. */
  back: { href: string; label: string };
};

export const deckMeta: DeckMeta = {
  session: "Session 0.5",
  title: "Visual Circuits: Three Foundational Claims",
  phase: "Phase 0 · Prelude",
  window: "The two weeks before W1 · Aug 30 – Sep 13, 2026",
  hours: "7h read · 4h code",
  source: "Original Distill Circuits Thread (2020-03 – 2021-04)",
  back: { href: "/session-0-5", label: "← Session 0.5 study page" },
};

export const slides: DeckSlide[] = [
  {
    kind: "cover",
    chapter: "Cover",
    title: "Visual Circuits: Three Foundational Claims",
    subtitle: "Before we open up a transformer, we work out what a “circuit explanation” is actually supposed to deliver — and what kind of evidence it needs.",
    meta: [
      "Aug 30 – Sep 13, 2026",
      "7h read · 4h code",
      "Output: a one-page evidence audit",
    ],
    note: "First 3 minutes: this is the only session all year where we read the ideas without reproducing the code. Its position is special — not a warm-up, but the session that sets the evidence standard for the 26 sessions that follow.",
  },
  {
    kind: "statement",
    chapter: "Context",
    title: "Where this session sits on the year's route",
    lead: "At the very bottom of transformer-circuits.pub there is one line: March 2020 – April 2021 · Original Distill Circuits Thread. It points to distill.pub/2020/circuits/ — the visual interpretability series on InceptionV1.",
    body: [
      "That thread is the intellectual origin of the whole Circuits research line. Every term you now use on transformers — feature, circuit, universality, superposition, equivariance — was first defined in those articles.",
      "But its toolchain (Lucid + TensorFlow 1.x) is obsolete. So the rule for this session is: **read the ideas, don't reproduce the code**. The only time all year.",
    ],
    aside: "Phase 0 → Phase I (Sessions 1–5) → … → Phase VI (Sessions 24–26)",
    note: "Emphasize: not reproducing the code doesn't mean not touching code. There are still 4 hours of lightweight coding — the goal is to see it with your own eyes, not to grind out a replication.",
  },
  {
    kind: "numbered",
    chapter: "Goals",
    title: "By the end of this session you should be able to",
    lead: "These five are the acceptance criteria for the session — and the content your one-page evidence audit needs to cover.",
    items: [
      { term: "Define feature and circuit by pointing at an example", text: "Not reciting definitions, but being able to say: “this is a mixed3b curve detector, built from earlier curve detectors weighted along the tangent direction.”" },
      { term: "Tell three strengths of evidence apart", text: "Visual evidence, correlational evidence, causal evidence. Know which kind feature visualization is — and be able to say why." },
      { term: "Explain how equivariance supports universality", text: "Rotated copies of the same feature show up as a family — a sign the model learned structure, not coincidence." },
      { term: "Make one CNN feature visualization yourself", text: "And record all five: model, layer, channel, method, hyperparameters. The recording is itself part of the training." },
      { term: "Write down what has not been proven", text: "For each claim, name one experiment that would change your confidence. This is the session's most important output." },
    ],
    note: "Item 5 is the hardest and the most critical. People tend to summarize what the papers say; what we want is a clear account of what the papers don't say.",
  },
  {
    kind: "links",
    chapter: "Readings",
    title: "Reading list I: four close reads",
    lead: "Of the 7-hour reading budget, these four take 5. Read section by section and take notes. Curve Detectors and Curve Circuits are a pair — read them back to back.",
    groups: [
      {
        label: "Close read",
        note: "Section by section, notes in hand, questions ready",
        items: [
          {
            title: "Zoom In: An Introduction to Circuits",
            meta: "Olah et al. · 2020-03-10",
            href: "https://distill.pub/2020/circuits/zoom-in/",
            text: "The manifesto of the whole series. The three speculative claims are laid out here.",
          },
          {
            title: "Curve Detectors",
            meta: "Cammarata et al. · 2020-06-17",
            href: "https://distill.pub/2020/circuits/curve-detectors/",
            text: "The exemplar of one feature cross-validated by seven lines of argument. Watch the methodology, not just the conclusions.",
          },
          {
            title: "Curve Circuits",
            meta: "Cammarata et al. · 2021-01-30",
            href: "https://distill.pub/2020/circuits/curve-circuits/",
            text: "Still the only circuit fully reverse-engineered down to the weights and reimplemented with handwritten ones.",
          },
          {
            title: "Multimodal Neurons in Artificial Neural Networks",
            meta: "Goh et al. · 2021-03-04",
            href: "https://distill.pub/2021/multimodal-neurons/",
            text: "The systematic record of polysemantic neurons — the direct impetus for Toy Models of Superposition.",
          },
        ],
      },
    ],
    note: "Remind everyone: Distill articles are heavy with interactive figures and read badly on phones — use a desktop browser and block out uninterrupted time.",
  },
  {
    kind: "links",
    chapter: "Readings",
    title: "Reading list II: skim & skippable",
    lead: "Budget 1.5h for skimming and 0.5h for looking up terms. Skimming means abstract, figures, conclusions — do not fall in.",
    groups: [
      {
        label: "Skim",
        note: "Abstract, figures, and conclusions are enough",
        items: [
          {
            title: "An Overview of Early Vision in InceptionV1",
            meta: "2020-04",
            href: "https://distill.pub/2020/circuits/early-vision/",
            text: "The complete feature catalog of the early vision layers. Flip through it like a field guide; don't read it cover to cover. **Stands in for the retired OpenAI Microscope — coding task A happens here.**",
          },
          {
            title: "Naturally Occurring Equivariance in Neural Networks",
            meta: "2020-12-08",
            href: "https://distill.pub/2020/circuits/equivariance/",
            text: "The systematic treatment of equivariance as a circuit motif. Look at the five types it catalogs.",
          },
          {
            title: "High-Low Frequency Detectors",
            meta: "2021-01-27",
            href: "https://distill.pub/2020/circuits/frequency-edges/",
            text: "A case study of a feature that is counterintuitive right up until it is understood — and then simple.",
          },
        ],
      },
      {
        label: "Skippable",
        note: "Not needed this session; come back when you need them",
        items: [
          { title: "Visualizing Weights", meta: "2021-02", text: "The methodological fine print of weight visualization." },
          { title: "Branch Specialization", meta: "2021-04", text: "The branch specialization phenomenon." },
          { title: "Weight Banding", meta: "2021-04", text: "The weight banding phenomenon." },
        ],
      },
    ],
    note: "The three skippable pieces aren't unimportant — their value only shows once you actually start staring at weights. Come back to Visualizing Weights when Session 2 has you plotting QK/OV heatmaps.",
  },
  {
    kind: "split",
    chapter: "Framing",
    title: "The authors' own analogy: Schwann's cell theory",
    left: {
      label: "Schwann's three claims about cells · 1839",
      text: [
        "1. The cell is the fundamental unit of structure, physiology, and organization in living things.",
        "2. Cells are both distinct entities and building blocks of organisms.",
        "3. Cells form by “free cell formation,” similar to how crystals grow.",
        "The first two hold to this day. **The third is spectacularly wrong.**",
      ],
    },
    right: {
      label: "Circuits' three claims about neural networks · 2020",
      text: [
        "1. **Features** are the fundamental unit of neural networks, corresponding to directions.",
        "2. Features are connected by weights, forming **circuits**.",
        "3. Analogous features and circuits recur across models and tasks — **universality**.",
        "The authors say it outright: these claims are speculative, and one of them may well turn out to be their Schwann's third.",
      ],
    },
    note: "This analogy is written into Zoom In by the authors themselves — I didn't add it. It sets the attitude for the whole session: a strong claim that might be wrong is worth more than a weak claim that must be right. It's also why the deliverable is an evidence audit, not book notes.",
  },
  {
    kind: "statement",
    chapter: "Claim 1",
    title: "Claim 1 · Features",
    lead: "Features are the fundamental unit of neural networks. They correspond to directions. They can be rigorously studied and understood.",
    body: [
      "“Direction” means a linear combination in a layer's activation space. Much of the time we talk directly about individual neurons; but the moment a neuron is **polysemantic**, we have to talk about directions instead of neurons.",
      "The genuinely contested part of this claim is not “directions exist” — that's trivial. It's the second half: **that they can be rigorously studied and understood**, and are therefore the right unit for understanding networks.",
      "The authors are candid that the research community splits here. One camp holds that networks mostly detect textures and imperceptible patterns, and that seemingly meaningful neurons are an illusion.",
    ],
    aside: "Zoom In §Claim 1",
    note: "Point everyone to the long footnote in Zoom In about the community split — it cites literature on both sides. That move — lay out the disagreement first, then give evidence — is itself worth learning.",
  },
  {
    kind: "cards",
    chapter: "Claim 1",
    title: "Three examples marshaled for Features",
    lead: "The authors deliberately picked three kinds: one intuitive, one counterintuitive, one high-level abstraction.",
    cards: [
      {
        label: "Ex 1",
        term: "Curve Detectors",
        text: "Ten neurons in mixed3b responding to curves and boundaries of roughly 60-pixel radius. They come as a family — each member detects a different orientation of the same feature, together tiling the full 360°. Sitting between the universally accepted (edge detectors) and the hotly contested (ears, cars, faces), they make the perfect test case.",
      },
      {
        label: "Ex 2",
        term: "High-Low Frequency Detectors",
        text: "One side of the receptive field looks for low frequency, the other for high. The authors didn't get it at first either; once understood, it's simple — one of the heuristics for detecting object boundaries, especially useful when the background is out of focus. **A natural, useful visual feature nobody predicted in advance.**",
      },
      {
        label: "Ex 3",
        term: "Pose-Invariant Dog Head Detector",
        text: "A high-level abstract feature. Its feature visualization is a geometry that is “physically impossible” yet highly informative, and dataset examples confirm it. At this level, running synthetic-stimulus experiments one by one gets expensive — exactly where circuit evidence starts to become essential.",
      },
    ],
    note: "Example 2 is the one I most want people to remember: a real payoff of interpretability is that models can teach us **abstractions we didn't already have**.",
  },
  {
    kind: "statement",
    chapter: "Claim 1",
    title: "But: Polysemantic Neurons",
    lead: "InceptionV1's 4e:55 responds to cat faces, car fronts, and cat legs — all at once.",
    body: [
      "To be precise: it is **not** responding to something cats and cars have in common. Feature visualization shows it separately looking for cat eyes and whiskers, for furry legs, for a shiny car front — there is no subtle shared feature.",
      "Why is this fatal? Connect a neuron with 5 meanings to another neuron with 5 meanings and you effectively have **25 connections that cannot be considered one at a time**. The combinatorics of circuit analysis explode.",
      "Already in 2020, the authors wrote down the direction of the eventual fix: “unfold” the network and resolve polysemantic neurons back into pure features. **That is exactly what SAEs do from Session 9 onward.**",
    ],
    aside: "Zoom In §Polysemantic Neurons",
    note: "This slide is the session's first junction with the main line. Have everyone flag it: the problem posed here doesn't get a theory until Session 6 (Toy Models), or a tool until Session 9.",
  },
  {
    kind: "statement",
    chapter: "Claim 2",
    title: "Claim 2 · Circuits",
    lead: "Features are connected by weights, forming circuits. These circuits can also be rigorously studied and understood.",
    body: [
      "A circuit is a **computational subgraph** of the network: a tightly linked set of features, plus the weighted edges between them in the original network. Usually on the order of ten features, though it can be larger.",
      "The authors' key sentence: once you understand which features sit at either end, **the floating-point weights of a neural network become meaningful**. You really can read algorithms off the weights.",
      "They expected to find a tangled mess. What they found instead was symmetric and richly structured.",
    ],
    aside: "Zoom In §Claim 2",
    note: "“Read meaningful algorithms off of the weights” — that sentence is the direct ancestor of the Sessions 2–3 mathematical framework: rewriting the transformer as a path expansion is likewise about making the weights legible.",
  },
  {
    kind: "cards",
    chapter: "Claim 2",
    title: "Three circuits, three lessons",
    cards: [
      {
        label: "Circuit 1",
        term: "The curve-detector circuit",
        text: "Low-level, spanning two layers. Full curve detectors are built mostly from **earlier curve detectors and line detectors**. Look at a single connection's 5×5 weights: the positive weights are arranged in the shape of a curve — meaning “at every point along the curve, look for an earlier curve running in the tangent direction.” Oppositely oriented early detectors inhibit at each point.",
      },
      {
        label: "Circuit 2",
        term: "Oriented dog-head detection",
        text: "High-level, spanning four layers. The network maintains mirrored left-facing and right-facing pathways, mutually inhibiting at every step to sharpen the contrast, finally merging into one pose-invariant neuron. The authors call this **unioning over cases**, and note that because the two paths inhibit each other, the circuit has an XOR-like character.",
      },
      {
        label: "Circuit 3",
        term: "Cars in superposition",
        text: "mixed4c has a car detector: wheels at the bottom of the convolutional window, windows at the top. But the next layer does **not** build another pure car detector — it smears the car feature across a batch of neurons whose main job is something else (dog detection). The authors named this **superposition**.",
      },
    ],
    note: "Circuit 3 is the most important card in this session. It shows polysemanticity is something the model does on purpose — it had a pure neuron and actively mixed it with other features. This leads straight to the central question of Toy Models of Superposition.",
  },
  {
    kind: "statement",
    chapter: "Claim 2",
    title: "Why a model does superposition",
    lead: "“At bottom this is a property of high-dimensional geometry: it permits only n orthogonal vectors, but exponentially many almost-orthogonal ones.”",
    body: [
      "The model does it to **conserve neurons**, saving them for more important work. As long as cars and dogs don't co-occur, it can still retrieve the dog feature accurately in later layers — and so the car feature gets stored without claiming a dedicated neuron.",
      "Commit that passage to memory. The entire Toy Models paper of Sessions 6–7 is that one paragraph turned into controlled experiments, phase diagrams, and fractional-dimension measurements.",
      "And mind the timeline: this explanation was written down in **March 2020**; Toy Models made it rigorous only in **September 2022**. Two and a half years apart.",
    ],
    aside: "Zoom In §Circuit 3: Cars in Superposition",
    note: "If people take one thing from this session, let it be this: superposition was not invented in the SAE era — it was observed and named back in the visual-circuits days.",
  },
  {
    kind: "bullets",
    chapter: "Claim 2",
    title: "Circuit motifs: abstract patterns that recur across circuits",
    lead: "By analogy with network motifs in systems biology — understand one motif and you get leverage on every graph that uses it. The authors suspect that in the long run, studying motifs may matter more than studying individual circuits.",
    items: [
      { term: "Equivariance", text: "Transformed copies of one feature appear as a family, with the weights rotating along. The symmetry of the problem shows up as symmetry in the weights." },
      { term: "Unioning over cases", text: "Detect several cases separately, then construct the invariant feature as their union — rather than simply “not caring about the details.”" },
      { term: "Superposition", text: "Spread one feature across several neurons whose main job is something else, to save capacity." },
    ],
    note: "The word motif keeps coming back: induction heads, name-mover heads, S-inhibition heads are all, at bottom, motif-hunting on transformers.",
  },
  {
    kind: "table",
    chapter: "Claim 2",
    title: "Five types of equivariance",
    lead: "From Naturally Occurring Equivariance (2020-12). Note the authors' honesty: some families have “holes” (a transformed copy missing), and some families' transformation sets are structurally weaker than a group.",
    head: ["Type", "Meaning", "Where it shows up"],
    rows: [
      ["Rotational", "Rotated copies of one feature, jointly tiling all orientations", "The curve-detector and high-low-frequency families"],
      ["Scale", "The same feature at different sizes, usually spread across layers", "Small in early layers, large in later ones"],
      ["Hue", "Hue variants of color features", "Center–surround with opposing hues, persisting into layers seven and eight"],
      ["Hue-Rotation", "Hue coupled to rotation: shifting hue 180° equals rotating 180°", "Units with opposing hues on either side"],
      ["Reflection", "Mirrored copies", "Left/right-facing feature pairs"],
    ],
    note: "Have the group hold this table up against Session 3's composition work — there we ask the same question: are different heads transformed copies of one algorithm?",
  },
  {
    kind: "statement",
    chapter: "Claim 3",
    title: "Claim 3 · Universality",
    lead: "Analogous features and circuits form across models and tasks.",
    body: [
      "The observations so far: curve detectors and high-low-frequency detectors appear in **AlexNet, InceptionV1, VGG19, and ResNetV2-50**; in models trained on Places365 rather than ImageNet; and again and again in plain convnets trained from scratch.",
      "But the authors write with striking restraint: “the only evidence we can offer today is **anecdotal** — we have not yet invested enough in the comparative study of features and circuits.”",
      "They also spell out what the rigorous version would take: characterize a set of features, then rigorously establish that **those very features** (not merely correlated ones) form across models; then find analogous features across layers of several models and show they connect into **the same weight structure**.",
    ],
    aside: "Zoom In §Claim 3",
    note: "Of the three claims this one has the weakest evidence — and it's the one people most easily overrate. In the audits, this is where I especially want to see low confidence scores.",
  },
  {
    kind: "quote",
    chapter: "Claim 3",
    title: "Why universality matters",
    quote: "Imagine a world where every species' cells had a completely different set of organelles and proteins. Would studying “the cell” even mean anything?",
    attribution: "Zoom In §Claim 3 (paraphrased)",
    body: [
      "If universality holds in its strongest sense, we can hope for a “periodic table of visual features” — something to go observe and catalog across models.",
      "If it mostly fails, we can only focus on the few models that genuinely matter to society — and pray they don't get replaced every year.",
      "The authors' point: being wrong here would not be fatal the way the first two would be, but it determines **what shape of circuits research is reasonable at all**.",
    ],
    note: "This question is personal for our group: we're about to spend a year learning methods on GPT-2 small. If universality fails, none of it transfers to Claude or Gemma. Everyone should genuinely sweat this for a moment.",
  },
  {
    kind: "numbered",
    chapter: "Evidence",
    title: "The seven arguments (I): four behavioral",
    lead: "These seven aren't curve-specific — they're a general toolbox. The first four look only at the neuron's behavior, never at how it is implemented inside.",
    items: [
      { term: "Feature Visualization", text: "Optimizing the input to fire the neuron strongly and reliably produces curves. This establishes a **causal** link: every element in the image was put there to make the neuron fire harder." },
      { term: "Dataset Examples", text: "The ImageNet images that fire it strongly are almost always curves of the expected orientation; the moderately activating ones are less perfect curves." },
      { term: "Synthetic Examples", text: "Hand-drawn curves get the expected response across orientation, curvature, and background; straight lines and sharp corners don't fire it much. Peak activations run **24 standard deviations** above the mean." },
      { term: "Joint Tuning", text: "Rotate dataset examples and the neuron gradually stops firing while the next orientation's detector picks up. Together they tile the full 360°." },
    ],
    note: "The point: argument 1 is the only causal one of the four; argument 2 is mere correlation. Get the group to articulate that distinction themselves.",
  },
  {
    kind: "numbered",
    chapter: "Evidence",
    title: "The seven arguments (II): three from circuits",
    lead: "The last three all rest on circuits — which means **Claim 2 loops back to support Claim 1**. This is where the whole Circuits program hangs together, and the step most worth learning.",
    items: [
      { term: "Feature Implementation", text: "The curve-detection algorithm can be **read out** of the circuit that builds it, and nothing in the circuit suggests a second cause of firing. (Though the authors concede: many small weights remain whose role is unclear.)" },
      { term: "Feature Use", text: "The downstream features that consume it are all naturally curve-related (circles, 3D curvature, spirals), and use it as you'd expect." },
      { term: "Handwritten Circuits", text: "A clean-room reimplementation built from the understanding of how it works — **every weight set by hand** — closely reproduces the original curve detectors. This is the “strongest validation standard” that recurs all year." },
    ],
    note: "Four behavioral arguments, three mechanistic ones — the rest of the year chases the latter. Also remind everyone: even all seven together cannot rule out rare secondary activations.",
  },
  {
    kind: "cards",
    chapter: "Evidence",
    title: "What “this neuron detects curves” actually claims",
    lead: "Curve Detectors splits one vague sentence into four claims that can each be falsified on its own. **Use it as a ready-made template for your evidence audit.**",
    cards: [
      { label: "1", term: "Causality", text: "It genuinely detects a curve feature, not some stimulus that happens to correlate with curves. Evidence: feature visualization and attribution visualizations — “running it backwards” produces curves." },
      { label: "2", term: "Generality", text: "It responds to a wide variety of curve stimuli, tolerates a broad range of radii, and is largely insensitive to surface properties like color, brightness, and texture. Evidence: invariance experiments with synthetic stimuli." },
      { label: "3", term: "Purity", text: "It is not polysemantic and has no meaningful secondary function. Weakly activating images (edges, corners) are natural extensions of a curve-detection algorithm. Evidence: hand-labeling dataset examples bucketed by activation strength." },
      { label: "4", term: "Family", text: "The curve neurons jointly cover every orientation of curve. Evidence: joint tuning curves and radial tuning curves." },
    ],
    note: "The audit asks for a paragraph each on Features / Circuits / Universality — which is really asking for this same four-way decomposition of each claim. Feel free to hand this slide out as the template.",
  },
  {
    kind: "bullets",
    chapter: "Evidence",
    title: "Don't read only the good news: weaknesses the papers admit",
    lead: "No evidence audit is complete without these. Every one comes from the original papers — this is not our critique.",
    items: [
      { term: "Curve detectors are not robust", text: "Tiny changes in orientation or curvature swing the activation wildly. The authors flag this as a general problem across neuron families, visible as early as conv2d0." },
      { term: "Firing is sparse to begin with", text: "On ImageNet they fire at only about **10%** of spatial positions, and usually weakly. Strong activation is the exception." },
      { term: "Plenty of small weights remain illegible", text: "The backbone algorithm can be read off the circuit, but the authors admit there are “many small weights whose role is unclear.”" },
      { term: "Seven arguments can't rule out rare secondary firing", text: "They establish that curves cause firing; they cannot exclude rare stimuli that still trigger weaker activations." },
      { term: "Universality has only anecdotal evidence", text: "See Claim 3 above. The authors laid out the rigorous test themselves — and to this day nobody has run it in full." },
    ],
    note: "This slide is the antidote to reading the session as “the Circuits school is amazing.” The real takeaway: they did extraordinarily careful work, and they knew exactly what they had not shown.",
  },
  {
    kind: "statement",
    chapter: "Reading 3",
    title: "Curve Circuits: pushing understanding to its limit",
    lead: "“We reverse-engineer a nontrivial, learned algorithm from the weights of a neural network, and use its core ideas to hand-craft — from scratch — an artificial artificial neural network that reimplements it.”",
    body: [
      "Read that sentence three times. The claim is not “we can visualize this feature,” nor “we can predict when it fires.” It is: **we understand it well enough to write the weights down directly, no gradient descent required.**",
      "The validation is direct: probe the handmade neurons and InceptionV1's natural ones with the same synthetic curve stimuli. Across a sweep of radii and orientations, the artificial curve neurons track the natural ones point for point.",
      "InceptionV1 implements curve detection in five convolutional layers — while in classical computer vision, curve detection in natural images is a hard problem, arguably still unsolved.",
    ],
    aside: "Curve Circuits · 2021-01-30",
    note: "This is the “strongest validation standard” the year keeps returning to. The audit asks: does this standard work on transformers? (Hint: Session 21 runs head-on into this question when we discuss the limits of attribution graphs.)",
  },
  {
    kind: "statement",
    chapter: "Reading 4",
    title: "Multimodal Neurons: the other face of polysemanticity",
    lead: "In CLIP, a single neuron responds to photographs, drawings, and written text of the same concept.",
    body: [
      "Most famous is the Spider-Man neuron found by Michael Petrov and Gabriel Goh. The authors set it beside neuroscience: the human brain has neurons that respond to one concept across viewing conditions (Quiroga et al. 2005 and 2009 — the so-called “Halle Berry neuron”).",
      "There are also region neurons (responding to geography) and emotion neurons — Nick Cammarata found that “complex” emotions decompose into simpler ones.",
      "**Typographic attacks**: Alec Radford first noticed CLIP learning to read; Chelsea Voss confirmed that writing words on objects fools the zero-shot classifier, and found such attacks in the wild. This is the price of “the model learned a real feature you didn't anticipate.”",
    ],
    aside: "Multimodal Neurons · 2021-03-04",
    note: "On the timeline this paper is the hinge: the polysemanticity it documents, plus the cars-in-superposition observation in Zoom In, together form the motivation for Toy Models of Superposition.",
  },
  {
    kind: "quote",
    chapter: "Method",
    title: "Interpretability as a natural science",
    quote: "Circuits sidesteps these difficulties: it focuses on subgraphs of a neural network small enough that rigorous empirical study becomes possible.",
    attribution: "Zoom In §Interpretability as a Natural Science (paraphrased)",
    body: [
      "The authors invoke Kuhn: interpretability looks like a **pre-paradigmatic** field — no consensus on what the object of study is, which methods apply, or how results should be judged.",
      "The usual responses are benchmarks or user studies. The authors propose a third path: treat neural networks as objects of empirical study, and make **falsifiable** assertions about them.",
      "For circuits, falsifiability is concrete: **if you understand a circuit, you should be able to predict what happens when you edit the weights.** The price: circuit-level assertions cover far less ground than claims about whole-model behavior.",
    ],
    note: "This slide explains why our outputs are runnable notebooks and explicit checks rather than book reports — and why every session requires writing down limitations.",
  },
  {
    kind: "cards",
    chapter: "Hands-on",
    title: "Coding tasks (about 4 hours, lightweight)",
    lead: "The original toolchain (Lucid + TensorFlow 1.x) no longer runs: TF 1.15 tops out at Python 3.7, and Colab now ships 3.12+. We use **Lucent** instead (the PyTorch port of Lucid) — it loads the **same inception5h weights** as Lucid / Microscope, with identical channel numbering.",
    cards: [
      {
        label: "A",
        term: "Surface the curve family with the notebook",
        text: "`00.5_inception_circuits.ipynb` (Colab; turn on a GPU). The point is that you are **not copying channel numbers**: you draw a batch of synthetic curves at different orientations, sweep all 480 channels of mixed3b, and let the family surface on its own. You'll see the top 10 channels land **entirely** in the 5×5 branch, their preferred orientations tiling the circle, and their response to straight lines at just 4%–43% of their response to curves — so they are not edge detectors.",
      },
      {
        label: "B",
        term: "Read the algorithm off the weights",
        text: "Notebook section 4: **multiply out** the 1×1 bottleneck and the 5×5 convolution (the version shown in the article), see which mixed3a channels excite 3b:379, then test a falsifiable prediction: nearby orientations excite, opposite ones inhibit.",
      },
      {
        label: "C",
        term: "Write down your doubts",
        text: "Rerun the 3b:379 feature visualization three times with different random seeds and see how much it changes. This is your firsthand material on the question “how strong is feature-visualization evidence, really?” — and the only exercise in the notebook with no answer key.",
      },
    ],
    note: "When presenting task B, put the weight on the exception: the same prediction gives r ≈ −0.53 to −0.69 on 3b:379 / 343 / 340, but only −0.17 on 3b:388. A rule that “mostly holds, with exceptions” is exactly the kind of thing to learn to report honestly in the audit.",
  },
  {
    kind: "statement",
    chapter: "Output",
    title: "Output: a one-page evidence audit",
    lead: "Organize it under three headings: Features / Circuits / Universality. Under each, write three things: the strongest evidence you found, one important limitation, and one experiment that would change your confidence.",
    body: [
      "Also attach: one feature visualization of your own (with the five parameters recorded), two annotated figures from the readings, and a confidence score for each claim.",
      "**And answer two questions:**",
      "(1) How strong is the evidence for each of Features / Circuits / Universality?",
      "(2) Does the Curve Circuits validation standard — reimplementation with handwritten weights — work on transformers?",
    ],
    aside: "Bring it to the Sep 13 meeting — unfinished is fine",
    note: "Question (2) comes back verbatim in Session 21, when we discuss the limits of attribution graphs. Have everyone archive the answer they write now and pull it back out then.",
  },
  {
    kind: "table",
    chapter: "Pacing",
    title: "The two-week timebox",
    lead: "Total budget: 11 hours. Pin it to your calendar, or it will get squeezed into the night before the meeting.",
    head: ["Slot", "What", "Time", "Deliverable"],
    rows: [
      ["W-1 D1–D2", "Close-read Zoom In; make term cards", "2.5h", "Glossary draft (6 terms)"],
      ["W-1 D3–D4", "Close-read Curve Detectors, focus on the seven arguments", "2h", "One-sentence summary per argument"],
      ["W-1 D5", "Skim Early Vision / Equivariance / High-Low Frequency", "1.5h", "Three lines of takeaways each"],
      ["W0 D1", "Close-read Curve Circuits + Multimodal Neurons", "1.5h", "First draft of question (2)"],
      ["W0 D2", "Browse the Distill Early Vision feature catalog (task A)", "1.5h", "Observation notes on the curve family"],
      ["W0 D3", "Feature visualization (tasks B + C)", "2.5h", "One figure + five parameters + doubt list"],
      ["W0 D4", "Write the evidence audit; pick two figures", "1.5h", "One-page audit ✅"],
    ],
    note: "Note the rows sum to 13 hours, over the 11-hour budget. That's deliberate — a first pass will almost certainly get stuck somewhere. If time runs short, cut depth from task A, never from the time spent writing the audit.",
  },
  {
    kind: "bullets",
    chapter: "Glossary",
    title: "This session's glossary",
    lead: "These six terms recur through the 26 sessions ahead. The definitions you write now will keep getting revised — so write down what you understand **now**.",
    items: [
      { term: "Feature", text: "A direction in activation space (a linear combination of neurons) corresponding to a property you can articulate. Note: not the same thing as “a neuron.”" },
      { term: "Circuit", text: "A computational subgraph of the network: a set of features plus the weighted edges between them in the original network." },
      { term: "Universality", text: "Analogous features and circuits recur across models and tasks. So far, anecdotal evidence only." },
      { term: "Equivariance", text: "Transformed copies of one feature appear as a family, with the weights transforming along. Transforming the input is equivalent to permuting within the family." },
      { term: "Polysemanticity", text: "A single neuron responding to several unrelated inputs. The main obstacle to circuit analysis." },
      { term: "Superposition", text: "Packing more features than there are dimensions into limited neurons, along almost-orthogonal directions. The cause of polysemanticity." },
    ],
    note: "Have everyone make flashcards (term on the front; on the back: a one-line definition / the math or the shape / when I'll use it). Session 1 adds 15 more.",
  },
  {
    kind: "closing",
    chapter: "Closing",
    title: "Where these threads pay off",
    lead: "This session is not a warm-up. Every question it plants gets a dedicated session later in the year.",
    items: [
      { term: "Sessions 6–7 · Toy Models of Superposition", text: "Turns the cars-in-superposition observation into controlled experiments: phase diagrams, fractional dimensions, geometric structure." },
      { term: "Sessions 9–16 · Dictionary learning & SAEs", text: "Builds what Zoom In called for: unfold the network, resolve polysemantic neurons back into pure features." },
      { term: "Sessions 20–21 · Circuit Tracing", text: "Carries circuits from vision CNNs to transformers; Session 21 answers head-on whether handwritten-weight reimplementation works there." },
      { term: "Session 3 · Composition / Session 24 · Attention", text: "Hunts for circuit motifs on transformers — the induction head is one." },
    ],
    footer: "At the Sep 13 meeting, 9:00 PM ET: bring your evidence audit and your visualization, and be ready to walk through one methodological choice, one failure or ambiguity, and one question you want the group to discuss.",
    note: "Closing line: the only way to fail this session is to read it as pop science. The test is simple — if your audit never once says “the authors did not prove this,” you haven't read closely enough.",
  },
];
